import { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  Snackbar,
  Slider,
  Chip,
  Grid,
  Card,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PrintIcon from '@mui/icons-material/Print';

// Components
import CompanySetup from '../components/CompanySetup';
import UploadSection from '../components/UploadSection';
import PortfolioSummary from '../components/PortfolioSummary';
import ExecutiveDashboard from '../components/ExecutiveDashboard';
import ErrorBoundary from '../components/ErrorBoundary';
import PaymentToggle from '../components/PaymentToggle';
import PaymentComparison from '../components/PaymentComparison';
import CFOAnalysis from '../components/CFOAnalysis';
import CFOCapitalAnalysis from '../components/CFOCapitalAnalysis';

// Chart Components
import SavingsProjectionChart from '../components/SavingsProjectionChart';
import EquipmentComparisonChart from '../components/EquipmentComparisonChart';
import WaterfallChart from '../components/WaterfallChart';
import PaybackHeatmap from '../components/PaybackHeatmap';
import BeforeAfterChart from '../components/BeforeAfterChart';
import YearlyProjectionTable from '../components/YearlyProjectionTable';

// Services
import { parseEASFile } from '../services/easParser';
import {
  calculateEASROI,
  calculateProjectionsWithEscalation,
  generatePortfolioBeforeAfterData,
  calculatePortfolioElectricityRate,
  DEFAULT_ESCALATION_RATE,
} from '../services/easCalculations';
import { calculateAllCFOMetrics } from '../services/cfoMetrics';
import {
  calculatePaymentMetrics,
  calculateUpfrontMetrics,
  calculateCFOAnalysis,
} from '../services/paymentCalculations';

// Note: PDF export removed in favor of browser-native print view
// See /eas-print route and EASPrintView component

/**
 * EASCalculator - Equipment Assessment ROI Calculator
 *
 * Multi-step workflow:
 * 1. Company Setup - Configure financial parameters
 * 2. Upload EAS - Download template, fill in equipment data, upload
 * 3. Results - View portfolio summary, CFO metrics, equipment breakdown
 */
export default function EASCalculator() {
  // Workflow state
  const [activeStep, setActiveStep] = useState(0);
  const [companyConfig, setCompanyConfig] = useState(null);
  const [easData, setEASData] = useState(null);
  const [roiResults, setROIResults] = useState(null);
  const [cfoMetrics, setCFOMetrics] = useState(null);

  // UI state
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [uploadError, setUploadError] = useState(null);

  // Payment mode toggle
  const [paymentMode, setPaymentMode] = useState('upfront');

  // Electricity price escalation rate (default 3%)
  const [escalationRate, setEscalationRate] = useState(DEFAULT_ESCALATION_RATE * 100);

  // Calculate payment metrics for both scenarios
  const paymentMetrics = useMemo(() => {
    if (!roiResults?.portfolio?.summary) return null;

    const { total_treatment_cost, total_annual_savings } = roiResults.portfolio.summary;

    return {
      threeYear: calculatePaymentMetrics(total_treatment_cost, total_annual_savings),
      upfront: calculateUpfrontMetrics(total_treatment_cost, total_annual_savings),
    };
  }, [roiResults]);

  // Calculate CFO analysis for PDF (NPV, capital preservation, accounting)
  const cfoAnalysis = useMemo(() => {
    if (!roiResults?.portfolio?.summary) return null;

    const { total_treatment_cost, total_annual_savings } = roiResults.portfolio.summary;
    const discountRate = (companyConfig?.discountRate || 8) / 100;

    return calculateCFOAnalysis(total_treatment_cost, total_annual_savings, discountRate);
  }, [roiResults, companyConfig]);

  // Calculate escalation projections
  const escalationData = useMemo(() => {
    if (!roiResults?.portfolio?.summary) return null;

    const { total_annual_value, total_treatment_cost } = roiResults.portfolio.summary;
    const rate = escalationRate / 100; // Convert from percentage

    return calculateProjectionsWithEscalation(
      total_annual_value,
      10, // 10-year projection
      rate,
      total_treatment_cost
    );
  }, [roiResults, escalationRate]);

  // Calculate before/after chart data for full portfolio (aggregates all equipment)
  const beforeAfterData = useMemo(() => {
    if (!roiResults?.individual || roiResults.individual.length === 0) return null;

    // Filter to valid equipment items
    const validEquipment = roiResults.individual.filter(eq => eq.valid);
    if (validEquipment.length === 0) return null;

    // Use portfolio aggregation for accurate multi-equipment totals
    return generatePortfolioBeforeAfterData({
      equipmentList: validEquipment,
      electricityRate: calculatePortfolioElectricityRate(validEquipment),
      projectionYears: 10,
      escalationRate: escalationRate / 100,
    });
  }, [roiResults, escalationRate]);

  const steps = ['Company Setup', 'Upload Equipment Data', 'View Results'];

  // Step 1: Company Setup Complete
  const handleCompanySetupComplete = (config) => {
    setCompanyConfig(config);
    setActiveStep(1);
  };

  // Step 2: File Upload
  const handleFileUpload = async (file) => {
    setIsProcessing(true);
    setError(null);
    setUploadError(null);

    try {
      // Step 1: Parse file
      console.log('Parsing file:', file.name);
      const parsed = await parseEASFile(file);

      // Step 2: Validate parsed data
      if (!parsed || typeof parsed !== 'object') {
        throw new Error('File parsing failed: invalid data structure');
      }

      if (!parsed.equipment || !Array.isArray(parsed.equipment) || parsed.equipment.length === 0) {
        throw new Error('No equipment data found in file. Please ensure the file contains equipment rows.');
      }

      console.log('Parsed equipment count:', parsed.equipment.length);
      setEASData(parsed);

      // Step 3: Calculate ROI (pass company config for blended electricity rate)
      console.log('Calculating ROI...');
      const roiCalc = calculateEASROI(parsed, companyConfig);

      // Step 4: Validate ROI results
      if (!roiCalc || !roiCalc.portfolio || !roiCalc.portfolio.summary) {
        console.error('Invalid ROI results:', roiCalc);
        throw new Error('ROI calculation failed: invalid results structure');
      }

      console.log('ROI calculation complete:', {
        equipment_count: roiCalc.portfolio.summary.total_equipment,
        annual_savings: roiCalc.portfolio.summary.total_annual_savings
      });
      setROIResults(roiCalc);

      // Step 5: Calculate CFO metrics
      console.log('Calculating CFO metrics...');
      const cfoCalc = calculateAllCFOMetrics(roiCalc, companyConfig);

      // Step 6: Validate CFO metrics
      if (!cfoCalc || !cfoCalc.tier1_ebitda || !cfoCalc.tier2_revenue) {
        console.error('Invalid CFO metrics:', cfoCalc);
        throw new Error('CFO metric calculation failed: missing essential tiers');
      }

      console.log('CFO metrics calculation complete');
      setCFOMetrics(cfoCalc);

      // Success - move to results
      setActiveStep(2);
    } catch (err) {
      // Enhanced error reporting
      const errorMessage = err.message || 'Failed to process file';
      console.error('File processing error:', {
        error: err,
        message: errorMessage,
        stack: err.stack,
        file: file.name
      });
      setError(errorMessage);
      setUploadError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleError = (message) => {
    setError(message);
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setCompanyConfig(null);
    setEASData(null);
    setROIResults(null);
    setCFOMetrics(null);
    setError(null);
    setUploadError(null);
    setPaymentMode('upfront');
    setEscalationRate(DEFAULT_ESCALATION_RATE * 100);
  };

  // Print View Handler - Opens browser-native print view
  const handlePrintView = () => {
    // Store data in sessionStorage for the print view
    const printData = {
      roiResults,
      cfoMetrics,
      paymentMetrics,
      companyConfig,
      cfoAnalysis,
      escalationRate,
      beforeAfterData,
    };
    sessionStorage.setItem('easPrintData', JSON.stringify(printData));

    // Open print view in new tab
    window.open('/eas-print', '_blank');
  };

  return (
    <Box sx={{ minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
        {/* Page Header - BOLD & REFINED */}
        <Box sx={{ mb: { xs: 3, md: 5 } }}>
          <Typography
            sx={{
              fontSize: { xs: '1.4rem', sm: '1.75rem', md: '2.5rem' },
              fontWeight: 700,
              letterSpacing: '-0.02em',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 1,
              position: 'relative',
              pb: 2,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: 80,
                height: 3,
                background: (theme) =>
                  `linear-gradient(90deg, ${theme.palette.primary.main}, transparent)`,
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark'
                    ? `0 0 10px ${theme.palette.primary.main}66`
                    : `0 0 10px ${theme.palette.primary.main}44`,
              },
            }}
          >
            Equipment Assessment ROI Calculator
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ fontSize: '0.95rem', maxWidth: 600 }}
          >
            {activeStep === 2
              ? 'Comprehensive ROI analysis with CFO-level financial metrics'
              : 'Upload your equipment data for comprehensive ROI analysis with CFO-level financial metrics'}
          </Typography>
        </Box>

        {/* Stepper - Hidden on results page */}
        {activeStep < 2 && (
          <Stepper
            activeStep={activeStep}
            sx={{
              mb: { xs: 3, md: 5 },
              '& .MuiStepConnector-line': {
                borderColor: 'primary.main',
                borderTopWidth: 2,
                transition: 'all 0.3s ease',
              },
              '& .MuiStepLabel-label': {
                fontSize: { xs: '0.75rem', sm: '1rem' },
                fontWeight: 600,
                transition: 'all 0.3s ease',
                display: { xs: 'none', sm: 'block' },
                '&.Mui-active': {
                  color: 'primary.main',
                  fontWeight: 700,
                  display: 'block',
                },
                '&.Mui-completed': {
                  color: 'text.primary',
                  fontWeight: 600,
                },
              },
              '& .MuiStepIcon-root': {
                color: 'grey.300',
                fontSize: '2rem',
                transition: 'all 0.3s ease',
                '&.Mui-active': {
                  color: 'primary.main',
                  filter: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'drop-shadow(0 0 8px rgba(197, 232, 125, 0.6))'
                      : 'drop-shadow(0 0 8px rgba(124, 179, 66, 0.5))',
                  transform: 'scale(1.1)',
                },
                '&.Mui-completed': {
                  color: 'primary.main',
                },
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {/* Step Content */}
        <Box>
          {activeStep === 0 && (
            <CompanySetup onComplete={handleCompanySetupComplete} />
          )}

          {activeStep === 1 && (
            <Box>
              {/* Back Button */}
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
                sx={{ mb: 3 }}
              >
                Back to Company Setup
              </Button>

              <UploadSection
                onFileUpload={handleFileUpload}
                onError={handleError}
                uploadError={uploadError}
              />

              {isProcessing && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  Processing your equipment data...
                </Alert>
              )}
            </Box>
          )}

          {activeStep === 2 && roiResults && cfoMetrics && (
            <ErrorBoundary onReset={handleReset}>
              <Box>
                {/* Action Buttons Row */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{
                      order: { xs: 2, sm: 1 },
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Upload Different File</Box>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Upload New</Box>
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PrintIcon />}
                    onClick={handlePrintView}
                    sx={{
                      order: { xs: 1, sm: 2 },
                      width: { xs: '100%', sm: 'auto' },
                      px: { xs: 2, sm: 3 },
                      py: 1,
                      fontWeight: 600,
                      fontSize: { xs: '0.8rem', sm: '0.875rem' },
                      boxShadow: 2,
                      '&:hover': {
                        boxShadow: 4,
                      },
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Print / Save as PDF</Box>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Print / PDF</Box>
                  </Button>
                </Box>

                {/* Payment Option Toggle - AT TOP */}
                <PaymentToggle
                  value={paymentMode}
                  onChange={setPaymentMode}
                  upfront={paymentMetrics?.upfront}
                  threeYear={paymentMetrics?.threeYear}
                  portfolioData={roiResults?.portfolio}
                  cfoMetrics={cfoMetrics}
                  companyConfig={companyConfig}
                  roiResults={roiResults}
                />

                {/* ============================================= */}
                {/* STRATEGIC FRAMING FIRST - CFO sees this first */}
                {/* ============================================= */}

                {/* CFO Capital Allocation Analysis - Strategic framing leads */}
                <CFOCapitalAnalysis
                  roiResults={roiResults}
                  cfoMetrics={cfoMetrics}
                  companyConfig={companyConfig}
                  paymentMode={paymentMode}
                />

                {/* ============================================= */}
                {/* MONEY - CFO Financial Impact Analysis         */}
                {/* ============================================= */}

                {/* Executive Dashboard - CFO Metrics (EBITDA, Revenue, Enterprise Value) */}
                <ExecutiveDashboard
                  cfoMetrics={cfoMetrics}
                  companyConfig={companyConfig}
                />

                {/* Financial Impact Waterfall */}
                <WaterfallChart cfoMetrics={cfoMetrics} />

                {/* 5-Year Value Projection Chart */}
                <SavingsProjectionChart
                  projections={roiResults.portfolio.projections}
                  treatmentCost={roiResults.portfolio.summary.total_treatment_cost}
                  paybackMonths={roiResults.portfolio.summary.weighted_average_payback_months}
                  annualSavings={roiResults.portfolio.summary.total_annual_savings}
                  totalAnnualValue={roiResults.portfolio.summary.total_annual_value}
                  paymentMode={paymentMode}
                  paymentMetrics={paymentMode === 'threeYear' ? paymentMetrics?.threeYear : null}
                />

                {/* Payment Comparison Card */}
                {paymentMetrics && (
                  <PaymentComparison
                    upfront={paymentMetrics.upfront}
                    threeYear={paymentMetrics.threeYear}
                    activeMode={paymentMode}
                  />
                )}

                {/* CFO Financial Analysis - Only shown in 3-year mode */}
                <CFOAnalysis
                  treatmentCost={roiResults.portfolio.summary.total_treatment_cost}
                  annualSavings={roiResults.portfolio.summary.total_annual_savings}
                  initialDiscountRate={companyConfig?.discountRate || 8}
                  paymentMode={paymentMode}
                />

                {/* Electricity Price Escalation Control */}
                <Box
                  sx={{
                    my: 4,
                    p: 3,
                    borderRadius: 2,
                    background: (theme) => theme.palette.mode === 'dark'
                      ? 'rgba(255, 107, 53, 0.08)'
                      : 'rgba(199, 70, 0, 0.10)',
                    border: (theme) => `1px solid ${theme.palette.mode === 'dark'
                      ? 'rgba(255, 107, 53, 0.2)'
                      : 'rgba(199, 70, 0, 0.25)'}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TrendingUpIcon sx={{ color: 'warning.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      Electricity Price Escalation
                    </Typography>
                    <Chip
                      label={`${escalationRate.toFixed(1)}% per year`}
                      size="small"
                      color="warning"
                      sx={{ ml: 'auto', fontWeight: 600 }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Electricity prices typically increase 2-4% annually. Adjust to see impact on long-term savings.
                  </Typography>
                  <Box sx={{ px: 2 }}>
                    <Slider
                      value={escalationRate}
                      onChange={(_, value) => setEscalationRate(value)}
                      min={0}
                      max={6}
                      step={0.5}
                      marks={[
                        { value: 0, label: '0%' },
                        { value: 2, label: '2%' },
                        { value: 3, label: '3%' },
                        { value: 4, label: '4%' },
                        { value: 6, label: '6%' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value}%`}
                      sx={{
                        color: 'warning.main',
                        '& .MuiSlider-thumb': {
                          '&:hover, &.Mui-focusVisible': {
                            boxShadow: (theme) => theme.palette.mode === 'dark'
                              ? '0 0 0 8px rgba(255, 107, 53, 0.16)'
                              : '0 0 0 8px rgba(199, 70, 0, 0.16)',
                          },
                        },
                        '& .MuiSlider-mark': {
                          backgroundColor: 'warning.light',
                        },
                        '& .MuiSlider-markLabel': {
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* Year-by-Year Projection Table with Escalation */}
                {escalationData && (
                  <YearlyProjectionTable
                    escalationData={escalationData}
                    electricityRate={calculatePortfolioElectricityRate(roiResults.individual?.filter(eq => eq.valid))}
                    paymentMode={paymentMode}
                    treatmentCost={roiResults.portfolio.summary.total_treatment_cost}
                  />
                )}

                {/* ============================================= */}
                {/* ENERGY - Before/After Treatment Impact        */}
                {/* ============================================= */}

                {/* Before/After Treatment Energy Impact Chart */}
                {beforeAfterData && (
                  <BeforeAfterChart beforeAfterData={beforeAfterData} />
                )}

                {/* ============================================= */}
                {/* IMPACT - ESG & Sustainability                 */}
                {/* ============================================= */}

                {/* ESG & Sustainability Impact */}
                {cfoMetrics?.esg && (
                  <Box
                    sx={{
                      my: 4,
                      p: 3,
                      borderRadius: 2,
                      background: (theme) => theme.palette.mode === 'dark'
                        ? 'rgba(76, 175, 80, 0.08)'
                        : 'rgba(46, 125, 50, 0.12)',
                      border: (theme) => `1px solid ${theme.palette.mode === 'dark'
                        ? 'rgba(76, 175, 80, 0.3)'
                        : 'rgba(46, 125, 50, 0.4)'}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : '#2E7D32',
                        }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : '#2E7D32' }}>
                        ESG & Sustainability Impact
                      </Typography>
                    </Box>

                    <Grid container spacing={{ xs: 2, md: 3 }}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ textAlign: 'center', p: { xs: 1.5, md: 2 } }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Carbon Reduction
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: { xs: '1.5rem', md: '2rem' },
                              fontWeight: 800,
                              color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : '#2E7D32',
                              fontFamily: '"JetBrains Mono", monospace',
                            }}
                          >
                            {(cfoMetrics.esg.carbonReduction || 0).toFixed(1)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            tonnes CO₂/year
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ textAlign: 'center', p: { xs: 1.5, md: 2 } }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Vehicle Equivalent
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: { xs: '1.5rem', md: '2rem' },
                              fontWeight: 800,
                              color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : '#2E7D32',
                              fontFamily: '"JetBrains Mono", monospace',
                            }}
                          >
                            {Math.round(cfoMetrics.esg.vehiclesRemoved || 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            cars removed from road
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Box sx={{ textAlign: 'center', p: { xs: 1.5, md: 2 } }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Trees Equivalent
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: { xs: '1.5rem', md: '2rem' },
                              fontWeight: 800,
                              color: (theme) => theme.palette.mode === 'dark' ? '#4CAF50' : '#2E7D32',
                              fontFamily: '"JetBrains Mono", monospace',
                            }}
                          >
                            {Math.round(cfoMetrics.esg.treesEquivalent || 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            trees planted equivalent
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                      CO₂ reductions directly support Scope 2 emissions reporting for ESG frameworks (GRI, SASB, CDP)
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        textAlign: 'center',
                        mt: 1,
                        fontStyle: 'italic',
                        opacity: 0.7,
                      }}
                    >
                      ESG benefits are ancillary and not included in financial approval criteria.
                    </Typography>
                  </Box>
                )}

                {/* ============================================= */}
                {/* EQUIPMENT - Breakdown and Analysis            */}
                {/* ============================================= */}

                {/* Energy & Treatment Metrics - Right before Equipment */}
                {roiResults?.portfolio?.summary && (
                  <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card
                        sx={{
                          p: { xs: 2, md: 3 },
                          borderTop: '3px solid',
                          borderColor: 'success.main',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            color: 'text.secondary',
                            mb: 1,
                          }}
                        >
                          Energy Saved
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: 'success.main',
                            fontFamily: '"JetBrains Mono", monospace',
                          }}
                        >
                          {(roiResults.portfolio.summary.total_kwh_saved || 0).toLocaleString()}
                          <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.secondary', ml: 0.5 }}>
                            kWh/year
                          </Typography>
                        </Typography>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <Card
                        sx={{
                          p: { xs: 2, md: 3 },
                          borderTop: '3px solid',
                          borderColor: 'success.main',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            color: 'text.secondary',
                            mb: 1,
                          }}
                        >
                          CO₂ Avoided
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: 'success.main',
                            fontFamily: '"JetBrains Mono", monospace',
                          }}
                        >
                          {((roiResults.portfolio.summary.total_kwh_saved || 0) * 0.00042).toFixed(1)}
                          <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'text.secondary', ml: 0.5 }}>
                            tonnes/year
                          </Typography>
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          = {Math.round(((roiResults.portfolio.summary.total_kwh_saved || 0) * 0.00042) / 4.6)} cars removed
                        </Typography>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <Card
                        sx={{
                          p: { xs: 2, md: 3 },
                          borderTop: '3px solid',
                          borderColor: paymentMode === 'threeYear' ? 'success.main' : 'warning.main',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            color: 'text.secondary',
                            mb: 1,
                          }}
                        >
                          {paymentMode === 'threeYear' ? 'Year 1 Payment' : 'Treatment Cost'}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                            color: paymentMode === 'threeYear' ? 'success.main' : 'warning.main',
                            fontFamily: '"JetBrains Mono", monospace',
                          }}
                        >
                          ${paymentMode === 'threeYear'
                            ? Math.round((roiResults.portfolio.summary.total_treatment_cost || 0) / 3).toLocaleString()
                            : (roiResults.portfolio.summary.total_treatment_cost || 0).toLocaleString()}
                        </Typography>
                        {paymentMode === 'threeYear' && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            of ${(roiResults.portfolio.summary.total_treatment_cost || 0).toLocaleString()} total (no interest)
                          </Typography>
                        )}
                      </Card>
                    </Grid>
                  </Grid>
                )}

                {/* Equipment Summary Stats - IMMEDIATELY before breakdown */}
                {roiResults?.portfolio?.summary && (
                  <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
                    {[
                      { label: 'Equipment', value: roiResults.portfolio.summary.total_equipment || 0, unit: 'units' },
                      { label: 'Total Tonnage', value: roiResults.portfolio.summary.total_tonnage?.toFixed(1) || '0', unit: 'tons' },
                      {
                        label: paymentMode === 'threeYear' ? 'Payback' : 'True Payback',
                        value: paymentMode === 'threeYear' ? 'None' : (roiResults.portfolio.summary.weighted_average_payback_months?.toFixed(1) || '0'),
                        unit: paymentMode === 'threeYear' ? '' : 'months',
                        subtitle: paymentMode === 'threeYear' ? 'Always profitable' : null
                      },
                      { label: '5-Year ROI', value: roiResults.portfolio.summary.five_year_roi?.toFixed(1) || '0', unit: '%' },
                    ].map((stat, idx) => (
                      <Grid item xs={12} sm={6} md={3} key={idx}>
                        <Card
                          sx={{
                            p: { xs: 2, md: 3 },
                            height: '100%',
                            borderTop: '3px solid',
                            borderColor: 'primary.main',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: (theme) =>
                                theme.palette.mode === 'dark'
                                  ? '0 8px 24px rgba(100, 181, 246, 0.2)'
                                  : '0 8px 24px rgba(25, 118, 210, 0.12)',
                              transform: 'translateY(-2px)',
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: '0.65rem', md: '0.75rem' },
                              fontWeight: 500,
                              letterSpacing: '0.05em',
                              textTransform: 'uppercase',
                              color: 'text.secondary',
                              mb: 1,
                            }}
                          >
                            {stat.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: { xs: '1.5rem', md: '2rem' },
                              fontWeight: 700,
                              lineHeight: 1,
                              color: 'primary.main',
                              fontFamily: '"JetBrains Mono", monospace',
                            }}
                          >
                            {stat.value}
                            <Typography
                              component="span"
                              sx={{ fontSize: { xs: '0.7rem', md: '0.875rem' }, fontWeight: 500, color: 'text.secondary', ml: 0.5 }}
                            >
                              {stat.unit}
                            </Typography>
                          </Typography>
                          {stat.subtitle && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontSize: { xs: '0.6rem', md: '0.7rem' } }}>
                              {stat.subtitle}
                            </Typography>
                          )}
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}

                {/* Equipment ROI Comparison Chart */}
                <EquipmentComparisonChart byType={roiResults.byType} />

                {/* Portfolio Summary - Integrated Equipment ROI Summary */}
                <PortfolioSummary
                  data={roiResults.portfolio}
                  byTypeData={roiResults.byType}
                  paymentMode={paymentMode}
                  paymentMetrics={paymentMetrics}
                />

                {/* Payback Heatmap by Age */}
                <PaybackHeatmap equipment={roiResults.individual} />

                {/* Estimate Disclaimer */}
                <Box
                  sx={{
                    mt: 4,
                    p: 3,
                    borderRadius: 2,
                    background: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.secondary' }}>
                    DATA SOURCES & METHODOLOGY
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                    This analysis uses a 6-component value model with varying levels of empirical support:
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1.5 }}>
                    <Typography component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: 0.5 }}>
                      <Box component="span" sx={{ display: 'inline-block', px: 0.75, py: 0.125, borderRadius: '4px', fontSize: '0.65rem', fontWeight: 700, color: '#fff', backgroundColor: 'success.main', mr: 0.75, lineHeight: 1.4 }}>PE-Certified</Box>
                      <strong>Energy Savings</strong> — Based on 129 PE-certified field measurements across 174 case studies (IPMVP-compliant).
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: 0.5 }}>
                      <Box component="span" sx={{ display: 'inline-block', px: 0.75, py: 0.125, borderRadius: '4px', fontSize: '0.65rem', fontWeight: 700, color: '#fff', backgroundColor: 'info.main', mr: 0.75, lineHeight: 1.4 }}>ASHRAE</Box>
                      <strong>Avoided Degradation</strong> — Based on ASHRAE 1998 Handbook Ch 2.9 degradation rates and 82 age-correlated corpus measurements.
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: 0.5 }}>
                      <Box component="span" sx={{ display: 'inline-block', px: 0.75, py: 0.125, borderRadius: '4px', fontSize: '0.65rem', fontWeight: 700, color: '#fff', backgroundColor: 'text.disabled', mr: 0.75, lineHeight: 1.4 }}>Industry Est.</Box>
                      <strong>Avoided Demand, Runtime, Life Extension, Mission-Critical</strong> — Estimated based on industry standards and engineering models. Actual results may vary by facility.
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
                    Equipment-specific efficiency improvements from corpus data: Chillers 21.1%, RTUs 22.3%, CRACs 26.1%, Split Systems 24.3%.
                    CFO financial metrics use configurable industry defaults and are not claims about treatment performance.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      Contact: sales@powertronglobal.com | powertronglobal.com
                    </Typography>
                    <Typography
                      component="a"
                      href="/eas-methodology.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ fontWeight: 600, color: 'primary.main', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                      Full Methodology →
                    </Typography>
                  </Box>
                </Box>

                {/* Reset Button */}
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleReset}
                    sx={{ px: 4 }}
                  >
                    Start New Analysis
                  </Button>
                </Box>
              </Box>
            </ErrorBoundary>
          )}
        </Box>

        {/* Error Snackbar - persistent until dismissed */}
        <Snackbar
          open={!!error}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setError(null)}
            severity="error"
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
